// src/providers/GlobalAIChatProvider.tsx
/**
 * GlobalAIChatProvider
 * Context provider for application-wide AI chat functionality
 */

import { useReducer, useCallback, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useAction, useQuery } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';
import type {
  IGlobalChatState,
  GlobalChatAction,
  IGlobalChatContextValue,
  IChatMessage,
  IChatContext,
  ISceneReference,
} from '@/@types/globalChat';
import { GlobalChatContext } from '@/contexts/globalChatContext';
import { usePageContext } from '@/hooks/usePageContext';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useKBChatSession } from '@/hooks/useKBChatSession';
import {
  parseSceneReference,
  buildClarificationMessage,
  buildAmbiguousMessage,
} from '@/utils/parseSceneReference';

// ============================================================================
// Types (temporary until queries are added in Task 5.1)
// ============================================================================

/** Scene data shape expected from getVideoScenes query */
interface ISceneData {
  _id: Id<'propertyScenes'>;
  sceneNumber: number;
  sceneType: string;
  voiceoverText: string;
  editedVoiceoverText?: string;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: IGlobalChatState = {
  messages: [],
  status: 'idle',
  error: null,
  isOpen: false,
  currentContext: null,
  pageContext: null,
  availableScenes: [],
  orgId: null,
  companyId: null,
  sessionId: null,
};

// ============================================================================
// Reducer
// ============================================================================

function chatReducer(state: IGlobalChatState, action: GlobalChatAction): IGlobalChatState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };

    case 'SET_STATUS':
      return { ...state, status: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, status: action.payload ? 'error' : state.status };

    case 'TOGGLE_OPEN':
      return { ...state, isOpen: !state.isOpen };

    case 'SET_OPEN':
      return { ...state, isOpen: action.payload };

    case 'SET_CONTEXT':
      return { ...state, currentContext: action.payload };

    case 'CLEAR_MESSAGES':
      return { ...state, messages: [] };

    case 'SET_PAGE_CONTEXT':
      return { ...state, pageContext: action.payload };

    case 'SET_AVAILABLE_SCENES':
      return { ...state, availableScenes: action.payload };

    case 'SET_ORG_CONTEXT':
      return { ...state, orgId: action.payload.orgId, companyId: action.payload.companyId };

    case 'UPDATE_LAST_MESSAGE': {
      const messages = [...state.messages];
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        messages[messages.length - 1] = { ...lastMessage, content: action.payload };
      }
      return { ...state, messages };
    }

    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.payload };

    case 'HYDRATE_MESSAGES':
      return { ...state, messages: action.payload };

    default:
      return state;
  }
}

// ============================================================================
// Provider Component
// ============================================================================

interface IGlobalAIChatProviderProps {
  children: ReactNode;
}

export function GlobalAIChatProvider({ children }: IGlobalAIChatProviderProps) {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  // URL-based page context detection
  const pageContext = usePageContext();

  // Org context for RAG mode (insurance/real-estate pages)
  const orgContext = useOrgContext();

  // Company context for company team members (forces KB mode)
  const companyContext = useCompanyContext();

  // Query latest video for property (when on property-video page)
  const latestVideo = useQuery(
    api.property.queries.getLatestVideoForProperty,
    pageContext.propertyId ? { propertyId: pageContext.propertyId } : 'skip'
  );

  // Query scenes for the video
  const scenes = useQuery(
    api.property.queries.getVideoScenes,
    latestVideo?._id ? { videoId: latestVideo._id } : 'skip'
  );

  // Update page context when URL changes
  useEffect(() => {
    dispatch({ type: 'SET_PAGE_CONTEXT', payload: pageContext });
  }, [pageContext]);

  // Update available scenes when scenes load
  useEffect(() => {
    // Type assertion: scenes will be ISceneData[] once getVideoScenes query is added (Task 5.1)
    const typedScenes = scenes as ISceneData[] | undefined; // AS_JUSTIFIED: getVideoScenes query returns ISceneData[] — typed via Task 5.1
    if (typedScenes && typedScenes.length > 0) {
      const sceneRefs: ISceneReference[] = typedScenes.map(scene => ({
        sceneId: scene._id,
        sceneNumber: scene.sceneNumber,
        sceneType: scene.sceneType,
        voiceoverPreview: (scene.editedVoiceoverText ?? scene.voiceoverText).slice(0, 50),
      }));
      dispatch({ type: 'SET_AVAILABLE_SCENES', payload: sceneRefs });
    } else {
      dispatch({ type: 'SET_AVAILABLE_SCENES', payload: [] });
    }
  }, [scenes]);

  // Sync org context into reducer state for RAG mode
  useEffect(() => {
    if (orgContext.isLoading || companyContext.isLoading) return;

    if (companyContext.isCompanyUser && companyContext.currentCompany) {
      // Company user → use parent org's ID + company ID for scoped queries
      dispatch({
        type: 'SET_ORG_CONTEXT',
        payload: {
          orgId: companyContext.currentCompany.orgId,
          companyId: companyContext.currentCompany._id,
        },
      });
    } else {
      dispatch({
        type: 'SET_ORG_CONTEXT',
        payload: {
          orgId: orgContext.currentOrg?._id ?? null,
          companyId: null,
        },
      });
    }
  }, [
    orgContext.isLoading,
    orgContext.currentOrg,
    companyContext.isLoading,
    companyContext.isCompanyUser,
    companyContext.currentCompany,
  ]);

  // Derived: whether the current page activates RAG mode
  const isKnowledgeBaseMode =
    pageContext.pageType === 'insurance' ||
    pageContext.pageType === 'real-estate' ||
    companyContext.isCompanyUser;

  // KB chat session persistence
  const kbContextType = useMemo(() => {
    if (!isKnowledgeBaseMode) return null;
    if (companyContext.isCompanyUser) return 'company-kb';
    return pageContext.pageType === 'insurance' ? 'insurance-kb' : 'real-estate-kb';
  }, [isKnowledgeBaseMode, companyContext.isCompanyUser, pageContext.pageType]);
  const resolvedOrgId =
    companyContext.isCompanyUser && companyContext.currentCompany
      ? companyContext.currentCompany.orgId
      : (orgContext.currentOrg?._id ?? null);
  const kbSession = useKBChatSession(kbContextType, resolvedOrgId);

  // Hydrate messages from DB on initial load (once per session)
  const hasHydrated = useRef(false);
  useEffect(() => {
    if (
      isKnowledgeBaseMode &&
      !hasHydrated.current &&
      !kbSession.isLoading &&
      kbSession.persistedMessages.length > 0
    ) {
      hasHydrated.current = true;
      dispatch({ type: 'HYDRATE_MESSAGES', payload: kbSession.persistedMessages });
    }
  }, [isKnowledgeBaseMode, kbSession.isLoading, kbSession.persistedMessages]);

  // Reset hydration flag when switching away from KB mode
  useEffect(() => {
    if (!isKnowledgeBaseMode) {
      hasHydrated.current = false;
    }
  }, [isKnowledgeBaseMode]);

  // Sync session ID into state
  useEffect(() => {
    dispatch({ type: 'SET_SESSION_ID', payload: kbSession.sessionId });
  }, [kbSession.sessionId]);

  // Convex actions
  const processSceneEditAction = useAction(api.chat.actions.processSceneEdit);
  const ragChatAction = useAction(api.knowledgeBase.chat.sendMessage);

  // Helper to create messages
  const createMessage = useCallback(
    (role: 'user' | 'assistant', content: string, context?: IChatContext): IChatMessage => ({
      id: crypto.randomUUID(),
      role,
      content,
      timestamp: Date.now(),
      context,
    }),
    []
  );

  // Send message - routes to appropriate handler based on context
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return;

      // Add user message immediately
      const userMessage = createMessage('user', content, state.currentContext ?? undefined);
      dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
      dispatch({ type: 'SET_STATUS', payload: 'sending' });

      try {
        // ── RAG branch: insurance, real-estate, and company user pages ─────
        if (isKnowledgeBaseMode) {
          if (!state.orgId) {
            const assistantMessage = createMessage(
              'assistant',
              'I need your organisation context to answer questions. Please ensure you are logged in to an organisation.'
            );
            dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
            dispatch({ type: 'SET_STATUS', payload: 'idle' });
            return;
          }

          // Persist user message to DB
          await kbSession.persistMessage('user', content.trim());

          // Build conversation history from current messages for multi-turn context
          const conversationHistory = state.messages.map(m => ({
            role: m.role,
            content: m.content,
          }));

          const result = await ragChatAction({
            message: content.trim(),
            orgId: state.orgId,
            companyId: state.companyId ?? undefined,
            conversationHistory,
          });

          const responseContent =
            result.success && result.response
              ? result.response
              : `Sorry, I couldn't process that request. ${result.error ?? 'Please try again.'}`;

          // Persist assistant response to DB
          await kbSession.persistMessage('assistant', responseContent);

          const assistantMessage = createMessage('assistant', responseContent);
          dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
          dispatch({ type: 'SET_STATUS', payload: 'idle' });
          return;
        }

        // ── Existing logic: property-video page ──────────────────────────────
        if (state.pageContext?.pageType === 'property-video' && state.availableScenes.length > 0) {
          // Parse message for scene reference
          const parseResult = parseSceneReference(content, state.availableScenes);

          if (parseResult.confidence === 'exact' && parseResult.sceneId) {
            // Found exact scene - call processSceneEdit
            const result = await processSceneEditAction({
              sceneId: parseResult.sceneId,
              content: content.trim(),
            });

            const responseContent = result.success
              ? result.response
              : `Sorry, I couldn't complete that request. ${result.error || 'Please try again.'}`;

            const assistantMessage = createMessage('assistant', responseContent);
            dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
          } else if (parseResult.confidence === 'ambiguous' && parseResult.ambiguousMatches) {
            // Ambiguous match - ask for clarification
            const clarification = buildAmbiguousMessage(parseResult.ambiguousMatches);
            const assistantMessage = createMessage('assistant', clarification);
            dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
          } else {
            // No scene reference found - ask for clarification
            const clarification = buildClarificationMessage(state.availableScenes);
            const assistantMessage = createMessage('assistant', clarification);
            dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
          }
        } else if (state.currentContext?.type === 'scene' && state.currentContext.entityId) {
          // Legacy: Scene context set via requestSceneEdit (keep for compatibility)
          const result = await processSceneEditAction({
            sceneId: state.currentContext.entityId as Id<'propertyScenes'>, // AS_JUSTIFIED: entityId is set by requestSceneEdit which only accepts Id<'propertyScenes'>
            content: content.trim(),
          });

          const responseContent = result.success
            ? result.response
            : `Sorry, I couldn't complete that request. ${result.error || 'Please try again.'}`;

          const assistantMessage = createMessage('assistant', responseContent);
          dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
        } else {
          // Not on a supported page or no scenes available
          const assistantMessage = createMessage(
            'assistant',
            'I can help you edit video scenes. Please navigate to the Property Video Generator (Step 5) to edit scenes, then tell me which scene you want to change (e.g., "Make scene 3 more exciting").'
          );
          dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
        }

        dispatch({ type: 'SET_STATUS', payload: 'idle' });
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to send message';
        dispatch({ type: 'SET_ERROR', payload: errorMsg });

        const errorMessage = createMessage(
          'assistant',
          `Sorry, something went wrong: ${errorMsg}`,
          state.currentContext ?? undefined
        );
        dispatch({ type: 'ADD_MESSAGE', payload: errorMessage });
      }
    },
    [
      createMessage,
      state.currentContext,
      state.pageContext,
      state.availableScenes,
      state.orgId,
      state.companyId,
      state.messages,
      isKnowledgeBaseMode,
      processSceneEditAction,
      ragChatAction,
      kbSession,
    ]
  );

  // Other actions
  const setContext = useCallback((context: IChatContext | null) => {
    dispatch({ type: 'SET_CONTEXT', payload: context });
  }, []);

  const toggleChat = useCallback(() => {
    dispatch({ type: 'TOGGLE_OPEN' });
  }, []);

  const openChat = useCallback(() => {
    dispatch({ type: 'SET_OPEN', payload: true });
  }, []);

  const closeChat = useCallback(() => {
    dispatch({ type: 'SET_OPEN', payload: false });
  }, []);

  const clearMessages = useCallback(() => {
    dispatch({ type: 'CLEAR_MESSAGES' });
  }, []);

  const deleteMessages = useCallback(async () => {
    await kbSession.deleteSession();
    dispatch({ type: 'CLEAR_MESSAGES' });
    dispatch({ type: 'SET_SESSION_ID', payload: null });
    hasHydrated.current = false;
  }, [kbSession]);

  const requestSceneEdit = useCallback(
    (sceneId: Id<'propertyScenes'>, editType: string) => {
      // Clear previous messages when switching context
      dispatch({ type: 'CLEAR_MESSAGES' });

      const context: IChatContext = {
        type: 'scene',
        entityId: sceneId,
        entityType: 'propertyScene',
        additionalData: { editType },
      };
      setContext(context);
      openChat();

      // Add a welcome message
      const systemMessage = createMessage(
        'assistant',
        `I'm ready to help you edit this scene. You can ask me to:\n\n• **Change the voiceover text** - "Change the text to..." or "Make it shorter"\n• **Update the video prompt** - "Change the camera motion to..."\n\nWhat would you like to change?`
      );
      dispatch({ type: 'ADD_MESSAGE', payload: systemMessage });
    },
    [setContext, openChat, createMessage]
  );

  // Memoize context value
  const value = useMemo<IGlobalChatContextValue>(
    () => ({
      messages: state.messages,
      status: state.status,
      error: state.error,
      isOpen: state.isOpen,
      currentContext: state.currentContext,
      pageContext: state.pageContext,
      availableScenes: state.availableScenes,
      orgId: state.orgId,
      companyId: state.companyId,
      sessionId: state.sessionId,
      isKnowledgeBaseMode,
      sendMessage,
      setContext,
      toggleChat,
      openChat,
      closeChat,
      clearMessages,
      deleteMessages,
      requestSceneEdit,
    }),
    [
      state.messages,
      state.status,
      state.error,
      state.isOpen,
      state.currentContext,
      state.pageContext,
      state.availableScenes,
      state.orgId,
      state.companyId,
      state.sessionId,
      isKnowledgeBaseMode,
      sendMessage,
      setContext,
      toggleChat,
      openChat,
      closeChat,
      clearMessages,
      deleteMessages,
      requestSceneEdit,
    ]
  );

  return <GlobalChatContext.Provider value={value}>{children}</GlobalChatContext.Provider>;
}
